/**
 * SecurityUpdateController
 *
 * Single decision point that renders one of:
 *   - <SecurityLockScreen /> (securityState === 'locked')
 *   - <SecurityUpdateModal /> (soft or hard, gated by useSecurityReminder)
 *   - null                   (none, verifying — verifying could later get a
 *                             splash; today it's invisible because the app
 *                             still boots normally during the splash window)
 *
 * Reads from VersionCheckContext so polling happens exactly once.
 */

import { useVersionCheckContext } from '../../context/VersionCheckContext';
import { useSecurityReminder } from '../../hooks/useSecurityReminder';
import SecurityUpdateModal from './SecurityUpdateModal';
import SecurityLockScreen from './SecurityLockScreen';

const SecurityUpdateController = () => {
  const {
    currentVersion,
    securityState,
    securityManifest,
    applyUpdate,
    newBuildTime,
  } = useVersionCheckContext();

  const { shouldShowModal, dismissible, markShown } = useSecurityReminder(securityState);

  if (securityState === 'locked') {
    // commitId / buildTime come from the running bundle's version info —
    // we don't carry those through securityManifest (which describes the
    // *threshold*, not the build). They're available on `newBuildTime`
    // (the last fetched manifest's buildTime) and via process-level data,
    // but we don't have commitId in this hook today. The threshold details
    // are what matters for compliance; commitId is best-effort.
    return (
      <SecurityLockScreen
        currentVersion={currentVersion}
        minVersion={securityManifest.minVersion}
        lockAt={securityManifest.lockAt}
        commitId={undefined}
        buildTime={newBuildTime}
        supportContact={securityManifest.supportContact}
        securityNotice={securityManifest.securityNotice}
        onUpdate={applyUpdate}
      />
    );
  }

  if (shouldShowModal) {
    const handleDismiss = () => {
      if (dismissible) markShown();
    };

    return (
      <SecurityUpdateModal
        currentVersion={currentVersion}
        minVersion={securityManifest.minVersion}
        lockAt={securityManifest.lockAt}
        securityNotice={securityManifest.securityNotice}
        dismissible={dismissible}
        onUpdate={applyUpdate}
        onDismiss={handleDismiss}
      />
    );
  }

  return null;
};

export default SecurityUpdateController;
